import {
  ESTADO_VITIMA_VAZIO,
  VITIMAS_POR_ID,
  avancarEstadoVitima,
  atualizarTempoSimulado,
  criarSessaoAtendimento,
  estagiosDaVitima,
  type FichaVitimaAtendimento,
  type SessaoAtendimentoTabletop,
} from '../../dominio/tabletop/atendimento'

export interface ConfiguracaoPiora {
  readonly ativa: boolean
  readonly intervaloSegundos: number
}

export interface ProgramacaoVitima {
  readonly configuracao: ConfiguracaoPiora
  readonly proximoEm: number | null
  readonly respostaRespiratoriaValidada: boolean
}

export interface AtendimentoOficina {
  readonly sessao: SessaoAtendimentoTabletop
  readonly programacoes: Readonly<Record<string, ProgramacaoVitima>>
}

/** Tempos do exercício, sem pretensão de prever a evolução de um paciente real. */
export function configuracaoInicialPiora(ficha: FichaVitimaAtendimento): ConfiguracaoPiora {
  return {
    ativa: ['vitima9', 'vitima3', 'vitima1', 'slide_49_v1_motorista'].includes(ficha.id),
    intervaloSegundos: ficha.id === 'vitima9' ? 240 : 180,
  }
}

export function programacaoDaVitima(atendimento: AtendimentoOficina, ficha: FichaVitimaAtendimento): ProgramacaoVitima {
  return atendimento.programacoes[ficha.id] ?? {
    configuracao: configuracaoInicialPiora(ficha), proximoEm: null, respostaRespiratoriaValidada: false,
  }
}

export function criarAtendimentoOficina(idVitima: string): AtendimentoOficina {
  return { sessao: criarSessaoAtendimento(idVitima), programacoes: {} }
}

/** Começa a contagem ao iniciar o relógio ou abrir uma nova vítima com ele rodando. */
export function prepararProgramacao(atendimento: AtendimentoOficina): AtendimentoOficina {
  const { sessao } = atendimento
  const ficha = VITIMAS_POR_ID[sessao.focoVitimaId ?? '']
  if (!sessao.cronometroRodando || !ficha) return atendimento
  const programacao = programacaoDaVitima(atendimento, ficha)
  if (!programacao.configuracao.ativa || programacao.proximoEm !== null || estagiosDaVitima(ficha).length < 2) return atendimento
  return { ...atendimento, programacoes: { ...atendimento.programacoes, [ficha.id]: {
    ...programacao, proximoEm: sessao.tempoSimuladoSegundos + programacao.configuracao.intervaloSegundos,
  } } }
}

export function configurarPiora(atendimento: AtendimentoOficina, ficha: FichaVitimaAtendimento, configuracao: ConfiguracaoPiora): AtendimentoOficina {
  if (!Number.isFinite(configuracao.intervaloSegundos)) return atendimento
  const anterior = programacaoDaVitima(atendimento, ficha)
  return prepararProgramacao({ ...atendimento, programacoes: { ...atendimento.programacoes, [ficha.id]: {
    ...anterior,
    configuracao: { ...configuracao, ativa: configuracao.ativa && estagiosDaVitima(ficha).length > 1,
      intervaloSegundos: Math.min(900, Math.max(60, Math.round(configuracao.intervaloSegundos))) },
    proximoEm: null,
  } } })
}

export function validarRespostaRespiratoria(atendimento: AtendimentoOficina, ficha: FichaVitimaAtendimento, validada: boolean): AtendimentoOficina {
  const programacao = programacaoDaVitima(atendimento, ficha)
  return { ...atendimento, programacoes: { ...atendimento.programacoes, [ficha.id]: {
    ...programacao, respostaRespiratoriaValidada: validada,
  } } }
}

export function motivoDaPiora(atendimento: AtendimentoOficina, ficha: FichaVitimaAtendimento): string | null {
  const estado = atendimento.sessao.estadosVitimas[ficha.id] ?? ESTADO_VITIMA_VAZIO
  if (ficha.id === 'vitima9') {
    return estado.problemas.hemorragia_coxa_direita === 'controlado' ? null : 'Hemorragia da coxa direita ainda sem controle.'
  }
  if (ficha.id === 'vitima3') {
    if (programacaoDaVitima(atendimento, ficha).respostaRespiratoriaValidada) return null
    const avaliou = ['b_frequencia_respiratoria', 'b_ausculta_pulmonar', 'b_expansao_toracica'].every(id => estado.acoesReveladas.some(a => a === id))
    return avaliou ? 'Respiração avaliada, mas a resposta à conduta ainda não foi validada.' : 'Avaliação respiratória incompleta e resposta à conduta ainda não validada.'
  }
  return 'Evolução prevista no roteiro deste cenário.'
}

/** Avança apenas com o relógio rodando; aplica no máximo uma etapa por vencimento. */
export function avancarRelogioOficina(atendimento: AtendimentoOficina): AtendimentoOficina {
  if (!atendimento.sessao.cronometroRodando) return atendimento
  let atual = prepararProgramacao(atendimento)
  const tempo = atual.sessao.tempoSimuladoSegundos + 1
  atual = { ...atual, sessao: atualizarTempoSimulado(atual.sessao, tempo) }
  for (const [id, programacao] of Object.entries(atual.programacoes)) {
    const ficha = VITIMAS_POR_ID[id]
    if (!ficha || !programacao.configuracao.ativa || programacao.proximoEm === null || tempo < programacao.proximoEm) continue
    const motivo = motivoDaPiora(atual, ficha)
    if (!motivo) continue
    const estagios = estagiosDaVitima(ficha)
    const estado = atual.sessao.estadosVitimas[id] ?? ESTADO_VITIMA_VAZIO
    const indice = estagios.findIndex(e => e.id === estado.estadoEvolucao)
    const proximo = indice >= 0 ? estagios[indice + 1] : undefined
    if (!proximo) continue
    const evoluida = avancarEstadoVitima(atual.sessao, id, proximo.id, proximo.rotulo)
    const [evento, ...historico] = evoluida.historicoEventos
    atual = {
      sessao: { ...evoluida, historicoEventos: [{ ...evento!, descricao: `Piora programada: ${proximo.rotulo}`, detalhes: motivo }, ...historico] },
      programacoes: { ...atual.programacoes, [id]: { ...programacao, proximoEm: tempo + programacao.configuracao.intervaloSegundos } },
    }
  }
  return atual
}
